/**
 * @class : FilterComponent
 * @author : Sharath Chalya Nagaraju
 * This is a generic component which can be used to display common filters.
 */
import {Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef} from '@angular/core';
import {FilterService} from './filter.service';

export interface DisplayFilter {
    name: string;
    code: string;
    filterItems: {
        name: string;
        code: string;
        count: number;
        extra?: any;
    }[];
    hasCustomTemplate?: boolean;
    shouldOpen?: boolean;
    rangeQuery?: string;
    rangeQueryType?: string;
}

@Component({
    selector: 'copart-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss']
})
export class FiltersComponent implements OnInit{


      @Input() translateObject: any;

    /**
     * This arrays specifies if the filters are expanded or not and each filter is index in the order in which
     * they are displayed
     */
     @Input() filtersState: boolean[];

    /**
     * This array holds the search text that the user has entered for each filter
     */
    public searchText: string [];

    /**
     * This boolean value specifies if all the filters are opened or not
     */
    @Input() public openAllFilters: boolean;

    /**
     * removes count label in the filters
     */
    @Input() disableCount: boolean;
    /**
     * This holds the information about the filters that is currently being displayed
     * this filter information is provided by the user of this component.
     */
    public _filters: DisplayFilter[];


    public hideFilterCountsForCodes: string [] = [];

    /**
     * This is the attribute which is exposed to the user of the component
     * using this the filters data is passed.
     *  val : filter data
     */
    @Input()
    public set filters(val: DisplayFilter[]) {
        if (val) {
            this._filters = val;
            this.filtersState = this._filters.map((x: any) => this.openAllFilters || x.shouldOpen
                || this.filterService.getFilter(x));
        }
    }

    @Input()
    public set hideFilterCounts(val: any) {
      this.hideFilterCountsForCodes = val.split(',');
    }

    /**
     * emits an event to clear the custom filter fields.
     *  filter:string : identifies the filters to which the clear field request was made.
     * if the clear request was for all the filters this field will be undefined,
     * else if the clear request was targeted for a particular filter the value stored in this variable will
     * match the filter header.
     */
    @Output() clearFilter: EventEmitter<DisplayFilter>;

    /**
     * Reference to custom filter template,
     * @important : Note that the filter template that is provided should have an input
     * field where the value of the filter is stored.
     */
    @ContentChild(TemplateRef,  {static : false}) customFilterTemplate;

    /**
     * enables time filter for date
     */
    @Input() showtimeFilter: boolean = false;
    

      /**
     * enables regional distance filter for Location adding defaults values for list and codes to enable , it will control by enable flag
     */
    
    @Input() regionalDistanceSupport = {
        enableRegionalDistance:false,
        regionalDistanceList : [
            {label: '10 km', value : 10},
            {label: '25 km', value : 25},
            {label: '50 km', value : 50},
            {label: '100 km', value : 100},
            {label: '150 km', value : 150}
        
        ],
        regionalDistanceDefault:50,
        codesToEnable:['LOC','LCITY','LZIP']

    } 
    regionalDistance =50;
    constructor(private filterService: FilterService) {
        this.clearFilter = new EventEmitter();
        this.searchText = [];
        this._filters = [];
        this.disableCount = false;
    }
    
    ngOnInit(){
        //To get if regional distance is enabled getting default distance
        this.regionalDistance =   (this.regionalDistanceSupport && this.regionalDistanceSupport.regionalDistanceDefault)?this.regionalDistanceSupport.regionalDistanceDefault:50;
    }
    /**
     * Collapses or opens all the filters
     */
    closeOpenAll(): void {
        this.openAllFilters = !this.openAllFilters;
        this.filtersState = this._filters.map(() => this.openAllFilters);
    }

    /**
     * Collapses or opens the winded based on the index passed
     *  {number} index : The index of the filter that needs to be collapsed
     */
    openSelf(index: number): void {
        this.filtersState[index] = !this.filtersState[index];
    }

    /**
     * Clears the search fields and check boxes of the all the filters
     */
    clearAllFilters() {
        this.searchText = [];
        this.filterService.clearAllFilters();
        this.clearFilter.emit();
    }

    /**
     * UnChecks and removes the currently unchecked filter item.
     *  {string} filter
     *  {boolean} isCustomFilter identifies if the filter is custom or not
     */
    clearSectionFilters(filter: DisplayFilter, isCustomFilter: boolean) {
        this.filterService.clearFilter(filter);
        if (isCustomFilter) {
            this.clearFilter.emit(filter);
        }
    }

    /**
     * Clears the search field of the filter whose index is passed
     *  {number} index : Index of the filter
     */
    clearSearchTextBox(index: number) {
        this.searchText[index] = '';
    }

    /**
     * Checks if the current item of the filter is applied or not
     *  {DisplayFilter} filter : filter whose item needs to be checked
     *  item : item which needs to be checked
     *  {boolean} : true/false
     */
    isFilterApplied(filter: DisplayFilter, item): boolean {
        const filterSet: any = this.filterService.getFilter(filter);
        return filterSet && filterSet.find(x => x.code === item.code) != null;
    }

    /**
     * Apply's the filter when a particular item is selected and emits an updateFilter event.
     *  {DisplayFilter} filter : filter which was applied
     *  item : item in the filter which was applied.
     */
    applyFilters(filter: DisplayFilter, item) {
        const sendRegionalDistance =  (this.regionalDistanceSupport.enableRegionalDistance && this.regionalDistanceSupport.codesToEnable.indexOf(filter.code)!=-1)
        // This is intend to send selected regional distance if enabled from application from input regionalDistanceSupport 
        if(sendRegionalDistance){
        item.extra = {regionalDistance:this.regionalDistance};
        }
        this.filterService.applyFilters(filter, item);
    }

    /**
     * If there is a custom filter attached to this filter, this function helps that filter update the filter
     * status
     *  {string} filter
     *  {(item: Set<string>) => void}
     */
    applyCustomFilter(filter: any) {
        return this.applyFilters(filter.filter, filter.item);
    }

    /**
     * 
     * This is custom fitler applying on changing the regional distance for location for WOM use only
     */

    applyCustomFilterDistance(filter) {
        const filterSet: any = this.filterService.getFilter(filter);
        if(filterSet){
        filterSet.forEach((item,index) => {
            item.extra = {regionalDistance:this.regionalDistance,updateDistanceOnly:true};
            this.filterService.applyFilters(filter,item,filterSet.length-1 === index);
        });
        }
    }

    isAnyFilterApplied(filter){
        const filterSet: any = this.filterService.getFilter(filter);
        return filterSet&&filterSet.length>0;
    }
    


    /**
     * Used to get the display label of each filter item
     *  item
     *  {any}
     */
    getItemLabel(item, filter) {
        let label = item.name;
        let hideCount: boolean;
        hideCount = filter && filter.code ? this.hideFilterCountsForCodes.indexOf(filter.code) > -1 : false;
        if (!this.disableCount && !hideCount) {
            label += ` (${item.count})`;
        }
        return label;
    }

    filterIdentity(index, filter) {
        return filter.id;
    }

    applyNewFilter(filter,val,filterIdx){
      if(filter&&val!=''){
        const item = {
        code: `${(filter.filterItems && filter.filterItems.length>0 )? filter.filterItems[0].code.split(':')[0]:'location_zip'}:${val}`,
        count: 7,
        name: val,
        }
        if(!this.isFilterAppliedByVal(filter,item)){
            this.applyFilters(filter,item);
            this.searchText[filterIdx]='';
        }
       
        } 
    }
    isFilterAppliedByVal(filter: DisplayFilter, item): boolean {
        const filterSet: any = this.filterService.getFilter(filter);
        return filterSet && filterSet.find(x => x.name === item.name) != null;
    }
}
